import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Seo from '../../components/Seo';
import PageHero from '../../components/PageHero';
import StoryCard from '../../components/StoryCard';
import { SkeletonGrid, EmptyState, ErrorState, Pagination } from '../../components/ui';
import { storiesApi } from '../../api/resources';

export default function StoryList() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') || '');
  const [state, setState] = useState({ loading: true, error: null, items: [], meta: null });

  const page = Number(params.get('page') || 1);
  const category = params.get('category') || '';
  const featured = params.get('featured') || '';
  const q = params.get('q') || '';

  useEffect(() => {
    let active = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    storiesApi
      .list({ page, per_page: 9, category: category || undefined, q: q || undefined, featured: featured || undefined })
      .then((res) => active && setState({ loading: false, error: null, items: res.items, meta: res.meta }))
      .catch((error) => active && setState({ loading: false, error, items: [], meta: null }));
    return () => {
      active = false;
    };
  }, [page, category, q, featured]);

  // Derive category chips from loaded stories (no dedicated categories endpoint).
  const categories = Array.from(
    new Map(
      state.items.filter((s) => s.category).map((s) => [s.category.slug, s.category])
    ).values()
  );

  function update(next) {
    const merged = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => {
      if (v) merged.set(k, v);
      else merged.delete(k);
    });
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  }

  return (
    <>
      <Seo title="Impact Stories" description="Read inspiring stories of service, advocacy, and impact from Zonta District 17 Area 1." />

      <PageHero
        eyebrow="Impact Stories"
        title="Stories that inspire change"
        subtitle="Real stories of how our members and clubs are changing lives in the community and beyond."
        image="/img/story-b.jpg"
      />

      <section className="container-page py-12">
        {/* Toolbar */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              update({ q: query });
            }}
            className="flex w-full max-w-md gap-2"
          >
            <input
              className="input"
              placeholder="Search stories…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search stories"
            />
            <button type="submit" className="btn-primary px-4">
              Search
            </button>
          </form>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => update({ category: '', featured: '' })}
              className={`pill cursor-pointer transition-colors ${!category && !featured ? 'bg-maroon-700 text-white' : 'bg-surface-gray text-ink-soft hover:bg-cream-300'}`}
            >
              All
            </button>
            <button
              onClick={() => update({ featured: '1', category: '' })}
              className={`pill cursor-pointer transition-colors ${featured ? 'bg-gold-400 text-maroon-900' : 'bg-surface-gray text-ink-soft hover:bg-cream-300'}`}
            >
              ★ Featured
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => update({ category: c.slug, featured: '' })}
                className={`pill cursor-pointer transition-colors ${category === c.slug ? 'bg-maroon-700 text-white' : 'bg-surface-gray text-ink-soft hover:bg-cream-300'}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {state.loading ? (
          // Ladder + gap mirror the loaded grid below exactly; if one changes
          // the other must, or the cards re-flow the moment the data lands.
          <SkeletonGrid count={6} columns="1-2-3" gap={6} />
        ) : state.error ? (
          <ErrorState error={state.error} onRetry={() => update({})} />
        ) : state.items.length === 0 ? (
          <EmptyState
            icon={q || category || featured ? '🔍' : '✦'}
            title={q || category || featured ? 'No matching stories' : 'No stories yet'}
            message={
              q
                ? `No results for “${q}”. Try a different search or clear your filters.`
                : category || featured
                  ? 'No stories match the selected filters.'
                  : 'No published stories yet — check back soon for inspiring updates.'
            }
            action={
              q || category || featured ? (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    update({ q: '', category: '', featured: '' });
                  }}
                  className="btn-outline"
                >
                  Clear filters
                </button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {state.items.map((s) => (
                <StoryCard key={s.id} story={s} />
              ))}
            </div>
            <Pagination meta={state.meta} onChange={(p) => update({ page: String(p) })} />
          </>
        )}
      </section>
    </>
  );
}
